/**
 * scoreEngine.ts — pure computation, no Firestore dependencies.
 *
 * Formula:
 *   S_OKR+Ind  = (0.60 × S_individual) + (0.40 × S_team)
 *   S_total    = (okrIndividualWeight × S_OKR+Ind) + (opticsWeight × S_OPTICS) + (characterWeight × S_Character)
 *   S_final    = clamp(S_calibrated + delta, 1, scale)
 */

import type { ReviewTemplate, SelfReviewData, ManagerReviewData, ComputedFormScores } from '../types';

/** Average all provided question scores for a criteria → criteria aggregate score */
export function aggregateQuestionScores(questionScores: number[]): number {
  if (!questionScores.length) return 0;
  const valid = questionScores.filter(s => s > 0);
  if (!valid.length) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

/** Compute OPTICS section score from per-criteria scores and template weights */
export function computeOpticsScore(
  criteriaScores: Record<string, number>,
  template: ReviewTemplate,
): number {
  return template.opticsCriteria.reduce((sum, c) => {
    const score = criteriaScores[c.name] ?? 0;
    return sum + score * (c.weight / totalOpticsWeight(template));
  }, 0);
}

/** Compute Character section score from per-criteria scores */
export function computeCharacterScore(
  criteriaScores: Record<string, number>,
  template: ReviewTemplate,
): number {
  const totalW = template.characterCriteria.reduce((s, c) => s + c.weight, 0) || 1;
  return template.characterCriteria.reduce((sum, c) => {
    const score = criteriaScores[c.name] ?? 0;
    return sum + score * (c.weight / totalW);
  }, 0);
}

/** Compute OKR individual score from KR self-scores (simple average) */
export function computeIndividualOkrScore(krScores: number[]): number {
  if (!krScores.length) return 0;
  const valid = krScores.filter(s => s > 0);
  if (!valid.length) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

/** Blend individual + team scores per template split */
export function computeBlendedOkrScore(
  individualScore: number,
  teamScore: number,
  split: { individual: number; team: number },
): number {
  return split.individual * individualScore + split.team * teamScore;
}

/** Compute total review score */
export function computeTotalScore(
  blendedOkr: number,
  opticsScore: number,
  characterScore: number,
  w: ReviewTemplate['weightings'],
): number {
  return w.okrIndividual * blendedOkr + w.optics * opticsScore + w.character * characterScore;
}

/** Apply bounded discretion adjustment */
export function applyDiscretionAdjustment(
  calibratedScore: number,
  delta: number,
  maxDelta: number,
  scale: number = 5,
): { finalScore: number; clampedDelta: number } {
  const clampedDelta = Math.max(-maxDelta, Math.min(maxDelta, delta));
  const finalScore = Math.max(1, Math.min(scale, calibratedScore + clampedDelta));
  return { finalScore, clampedDelta };
}

/** Full score computation from self-review data + team scorecard score */
export function computeFormScores(
  selfReview: Partial<SelfReviewData>,
  managerReview: Partial<ManagerReviewData>,
  teamScore: number,
  template: ReviewTemplate,
): ComputedFormScores {
  // KR scores: prefer manager scores, fall back to self
  const krScores =
    (managerReview.krScores ?? []).map(k => k.managerScore).filter(Boolean).length > 0
      ? (managerReview.krScores ?? []).map(k => k.managerScore)
      : (selfReview.krProgress ?? []).map(k => k.selfScore);

  const indOkrScore = computeIndividualOkrScore(krScores);
  const blendedOkr = computeBlendedOkrScore(indOkrScore, teamScore, template.teamVsIndividualSplit);

  // OPTICS: prefer manager review scores
  const opticsScores =
    Object.keys(managerReview.opticsScores ?? {}).length > 0
      ? (managerReview.opticsScores ?? {})
      : (selfReview.opticsScores ?? {});

  const opticsScore = computeOpticsScore(opticsScores, template);

  const characterScores =
    Object.keys(managerReview.characterScores ?? {}).length > 0
      ? (managerReview.characterScores ?? {})
      : (selfReview.characterScores ?? {});

  const charScore = computeCharacterScore(characterScores, template);
  const total = computeTotalScore(blendedOkr, opticsScore, charScore, template.weightings);

  return {
    individualOkrScore: indOkrScore,
    teamScore,
    okrIndividualScore: blendedOkr,
    opticsScore,
    characterScore: charScore,
    totalScore: parseFloat(total.toFixed(3)),
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function totalOpticsWeight(template: ReviewTemplate): number {
  return template.opticsCriteria.reduce((s, c) => s + c.weight, 0) || 1;
}
